import type { UserCommunication } from '@this+that/models';

import type { ComposeSnapshotIndexEntry } from './types';
import { resolveDraftCommunication } from '../../utils/userCommunicationHelpers';

export const collectDraftIds = (uc: UserCommunication): Set<string> => {
  const ids = new Set<string>();
  if (uc.draftId) {
    ids.add(uc.draftId);
  }
  const nested = resolveDraftCommunication(uc)?.draftId;
  if (nested) {
    ids.add(nested);
  }
  uc.threadMessages?.forEach((message) => {
    if (message.draftId) {
      ids.add(message.draftId);
    }
  });
  return ids;
};

const collectMessageIds = (uc: UserCommunication): Set<string> => {
  const ids = new Set<string>();
  if (uc.messageId) {
    ids.add(uc.messageId);
  }
  uc.threadMessages?.forEach((message) => {
    if (message.messageId) {
      ids.add(message.messageId);
    }
  });
  return ids;
};

export interface SnapshotVisibility {
  openComposeSessionIds?: Record<string, true>;
  backgroundSavingSessionIds?: Record<string, true>;
  syncingSessionIds?: Record<string, true>;
  syncedDraftIdBySessionId?: Record<string, string>;
}

export const resolveLineageId = (entry: ComposeSnapshotIndexEntry): string =>
  entry.lineageId ?? entry.sessionId;

// Only a compose's own session is hidden while it is open and not saving; a restored source stays listed.
export const isSnapshotHidden = (
  sessionId: string,
  {
    openComposeSessionIds,
    backgroundSavingSessionIds,
  }: SnapshotVisibility = {},
): boolean =>
  Boolean(openComposeSessionIds?.[sessionId]) &&
  !backgroundSavingSessionIds?.[sessionId];

const groupByLineage = (
  entries: ComposeSnapshotIndexEntry[],
): ComposeSnapshotIndexEntry[][] => {
  const groups = new Map<string, ComposeSnapshotIndexEntry[]>();
  for (const entry of entries) {
    const lineageId = resolveLineageId(entry);
    groups.set(lineageId, [...(groups.get(lineageId) ?? []), entry]);
  }
  return [...groups.values()];
};

const hasServerDraft = (
  lineage: ComposeSnapshotIndexEntry[],
  { syncedDraftIdBySessionId }: SnapshotVisibility,
): boolean =>
  lineage.some(
    (entry) =>
      Boolean(entry.draftId) ||
      Boolean(syncedDraftIdBySessionId?.[entry.sessionId]) ||
      Boolean(syncedDraftIdBySessionId?.[resolveLineageId(entry)]),
  );

const newest = (
  entries: ComposeSnapshotIndexEntry[],
): ComposeSnapshotIndexEntry | undefined =>
  entries.reduce<ComposeSnapshotIndexEntry | undefined>(
    (best, entry) => (!best || entry.updatedAt > best.updatedAt ? entry : best),
    undefined,
  );

// One card per draft lineage: a saving session speaks for the lineage, otherwise the newest visible one.
const pickRepresentative = (
  lineage: ComposeSnapshotIndexEntry[],
  visibility: SnapshotVisibility,
): ComposeSnapshotIndexEntry | undefined => {
  const visible = lineage.filter(
    (entry) => !isSnapshotHidden(entry.sessionId, visibility),
  );
  const saving = visible.filter(
    (entry) => visibility.syncingSessionIds?.[entry.sessionId],
  );
  return newest(saving.length > 0 ? saving : visible);
};

export const selectUnsavedSnapshotEntry = (
  entriesBySessionId: Record<string, ComposeSnapshotIndexEntry>,
  uc: UserCommunication,
  visibility: SnapshotVisibility = {},
): ComposeSnapshotIndexEntry | undefined => {
  const draftIds = collectDraftIds(uc);
  const messageIds = collectMessageIds(uc);
  return newest(
    Object.values(entriesBySessionId).filter(
      (entry) =>
        !isSnapshotHidden(entry.sessionId, visibility) &&
        ((entry.draftId !== undefined && draftIds.has(entry.draftId)) ||
          (entry.sourceMessageId !== undefined &&
            messageIds.has(entry.sourceMessageId))),
    ),
  );
};

export const selectLocalOnlyEntries = (
  entriesBySessionId: Record<string, ComposeSnapshotIndexEntry>,
  visibility: SnapshotVisibility = {},
): ComposeSnapshotIndexEntry[] =>
  groupByLineage(Object.values(entriesBySessionId))
    .filter((lineage) => !hasServerDraft(lineage, visibility))
    .map((lineage) => pickRepresentative(lineage, visibility))
    .filter((entry): entry is ComposeSnapshotIndexEntry => Boolean(entry))
    .sort((a, b) => b.updatedAt - a.updatedAt);
